import React, { useState } from 'react';

import { c } from 'ttag';

import { Badge, Checkbox, CollapsibleSection, Table, TableBody, TableCell, TableRow } from '@freelancelabs/components';

import { AccountActivitiesQuery } from '../../../graphql/__generated__/types';

type Activities = NonNullable<AccountActivitiesQuery['accountActivities']>;
type Option = Activities['needs'][number];

interface Props {
    activities: Activities;
    needIds: string[];
    serviceIds: string[];
    onToggleNeed: (id: string, checked: boolean) => void;
    onToggleService: (id: string, checked: boolean) => void;
    onToggleMarket: (serviceIds: string[], checked: boolean) => void;
}

/** The market is only a grouped checkbox: the screen sends the list of services, never the market. */
const ActivityPicker = ({ activities, needIds, serviceIds, onToggleNeed, onToggleService, onToggleMarket }: Props) => {
    const checkedNeeds = new Set(needIds);
    const checkedServices = new Set(serviceIds);

    // Only source-designated activities show, plus any already checked: an edit screen must
    // keep a hand-granted activity visible, else the next save drops it silently.
    const keepNeed = (option: Option) => option.linkedToContact || checkedNeeds.has(option.id as string);
    const keepService = (option: Option) => option.linkedToContact || checkedServices.has(option.id as string);

    const visibleNeeds = activities.needs.filter(keepNeed);
    const visibleGroups = activities.marketGroups
        .map((group) => ({ ...group, services: group.services.filter(keepService) }))
        .filter((group) => group.services.length > 0);

    const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
    const isOpen = (key: string) => openKeys.has(key);
    const toggleOpen = (key: string, open: boolean) =>
        setOpenKeys((prev) => {
            const next = new Set(prev);
            if (open) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });

    const countLabel = (checkedCount: number, total: number) =>
        checkedCount > 0 && checkedCount < total ? `${checkedCount}/${total}` : String(total);

    const row = (option: Option, checked: boolean, toggle: (id: string, checked: boolean) => void) => (
        <TableRow key={option.id}>
            <TableCell>
                <Checkbox
                    id={`activity-${option.id}`}
                    checked={checked}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        toggle(option.id as string, event.target.checked)
                    }
                >
                    <span className="flex flex-wrap items-center gap-2">
                        <span>{option.name ?? c('Onboarding activity').t`Unnamed`}</span>
                        {option.linkedToContact && (
                            <Badge category="info" size="small">
                                {c('Onboarding activity').t`Named by the source`}
                            </Badge>
                        )}
                    </span>
                </Checkbox>
            </TableCell>
            <TableCell className="text-end align-middle">
                {option.displayStatus && <span className="text-xs text-text-600">{option.displayStatus}</span>}
            </TableCell>
        </TableRow>
    );

    const checkedNeedCount = visibleNeeds.filter((n) => checkedNeeds.has(n.id as string)).length;

    return (
        <div className="flex flex-col gap-2">
            {(activities.omittedNeeds > 0 || activities.omittedServices > 0) && (
                <p className="text-xs text-danger-700" role="alert">
                    {c('Onboarding activity')
                        .t`This account carries more activities than this list shows. Narrow the search to reach the ones missing.`}
                </p>
            )}

            <CollapsibleSection
                className="fl-collapsible-section--group fl-collapsible-section--white-head"
                open={isOpen('needs')}
                onOpenChange={(open) => toggleOpen('needs', open)}
                title={c('Onboarding activity').t`Needs`}
                count={
                    <Badge category="default" size="small">
                        {countLabel(checkedNeedCount, visibleNeeds.length)}
                    </Badge>
                }
            >
                {visibleNeeds.length === 0 ? (
                    <p className="px-2.5 py-2 text-xs text-text-600">{c('Onboarding activity')
                        .t`No need is named by the source for this address.`}</p>
                ) : (
                    <div className="max-h-[340px] overflow-auto">
                        <Table>
                            <TableBody>
                                {visibleNeeds.map((option) =>
                                    row(option, checkedNeeds.has(option.id as string), onToggleNeed)
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CollapsibleSection>

            {visibleGroups.length === 0 ? (
                <p className="px-2.5 py-2 text-xs text-text-600">{c('Onboarding activity')
                    .t`No service is named by the source for this address.`}</p>
            ) : (
                visibleGroups.map((group) => {
                    const key = group.marketId ?? 'no-market';
                    const ids = group.services.map((s) => s.id as string);
                    const checkedCount = ids.filter((id) => checkedServices.has(id)).length;
                    const allChecked = ids.length > 0 && checkedCount === ids.length;
                    // A market without a name and no market at all are two different sentences.
                    const label =
                        group.marketId === null
                            ? c('Onboarding activity').t`Without a framework agreement`
                            : (group.marketName ?? c('Onboarding activity').t`Market without a name`);
                    return (
                        <CollapsibleSection
                            key={key}
                            className="fl-collapsible-section--group fl-collapsible-section--white-head"
                            open={isOpen(key)}
                            onOpenChange={(open) => toggleOpen(key, open)}
                            title={label}
                            count={
                                <Badge category="default" size="small">
                                    {countLabel(checkedCount, ids.length)}
                                </Badge>
                            }
                        >
                            {/* The "whole market" box lives in the panel: inside a summary the
                                click would collapse the section. It only ticks what is under it. */}
                            <div className="border-b border-border-norm px-2.5 py-2">
                                <Checkbox
                                    id={`market-${key}`}
                                    checked={allChecked}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        onToggleMarket(ids, event.target.checked)
                                    }
                                >
                                    <span className="text-xs font-medium text-text-700">
                                        {c('Onboarding activity').t`Select every service shown here`}
                                    </span>
                                </Checkbox>
                            </div>
                            <div className="max-h-[340px] overflow-auto">
                                <Table>
                                    <TableBody>
                                        {group.services.map((option) =>
                                            row(option, checkedServices.has(option.id as string), onToggleService)
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CollapsibleSection>
                    );
                })
            )}
        </div>
    );
};

export default ActivityPicker;
